// Client-side SQLite schema cache, keyed by connection and scoped to one
// database file.
//
// TIERED LOAD (SLICES.md § S7 items 1-4, 7-8 — the MSSQL design of
// `Docs/IntelliSenseSnapshotLoad.md` applied to sqlite):
//
//   phase 1 — the `sqlite_master` relation list (own database + every
//             ATTACHed one) plus the `sqlite_version()` and
//             `PRAGMA database_list` probes. Published to the schema bus the
//             moment it lands, which flips the IntelliSense pill to ready and
//             makes relation-name completion work. `ensureSqliteSchema`
//             resolves here.
//   phase 2 — the per-table PRAGMAs (`table_info`, `index_list` +
//             `index_info`, `foreign_key_list`), walked in batches, merged
//             into the SAME snapshot object and re-published as each batch
//             lands (`whenSqliteSnapshotComplete` awaits it).
//
// SQLITE DIVERGENCE — there is no bulk column catalog to page, so the batch of
// relations IS the page (`SQLITE_PRAGMA_BATCH_OBJECTS`), with the event-loop
// yield between batches. Everything still goes through the shared
// per-connection introspection lane (`introspectionSemaphore.ts`, max 2 in
// flight) so the PRAGMA walk cannot starve a user query.

import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { projectSqliteSnapshot } from '@/features/intellisense/schemaBus/adapters/sqliteAdapter';

import { INTROSPECTION_PAGE_ROWS, yieldToEventLoop } from './introspectionPaging';
import {
  fetchSqliteAttachedDatabases,
  fetchSqliteRelationDetailBatches,
  fetchSqliteRelationPages,
  fetchSqliteServerVersion,
  type SqliteHydratedRelation,
  type SqliteRelationEntry,
} from './sqliteSchemaIntrospection';
import type { SqliteAttachedDatabase, SqliteSchemaSnapshot } from './sqliteSchemaTypes';
import {
  applySqliteRelationBatch,
  carryOverSqliteIntrospection,
  createSqliteLiveSnapshot,
  type SqliteLiveSnapshot,
} from './sqliteSnapshotMerge';
import { beginIntrospectionLoad } from './introspectionTruncation';
import { SNAPSHOT_STALE_AFTER_MS } from './snapshotLifetime';
import {
  createLoadFailures,
  createTieredCacheEntry,
  isEntryFresh,
  markLoadOutcome,
  publishDegraded,
  publishThrottled,
  recordLoadFailure,
  reportPartialSnapshot,
  whenEntryColumnsLoaded,
  whenEntryComplete,
  type LoadFailures,
  type TieredCacheEntry,
} from './tieredSnapshotCache';

type CacheEntry = TieredCacheEntry<SqliteSchemaSnapshot>;

const cacheByConnectionId = new Map<string, CacheEntry>();
// S7 item 3 — the snapshot is built once per connection and rebuilt only on
// DDL invalidation, explicit `forceRefresh`, or reconnect. This is a
// day-scale backstop, NOT a refresh cadence (was 30_000, which re-ran the
// whole `sqlite_master` + per-table PRAGMA fan-out while the user was
// editing).
const DEFAULT_TTL_MS = SNAPSHOT_STALE_AFTER_MS;


function entryFor(connectionId: string): CacheEntry {
  const existing = cacheByConnectionId.get(connectionId);
  if (existing) return existing;
  const created = createTieredCacheEntry<SqliteSchemaSnapshot>();
  cacheByConnectionId.set(connectionId, created);
  return created;
}

export function getCachedSqliteSchema(connectionId: string): SqliteSchemaSnapshot | undefined {
  return cacheByConnectionId.get(connectionId)?.snapshot;
}

export function invalidateSqliteSchema(connectionId: string): void {
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  // Bump the generation so any refresh already in flight cannot publish its
  // pre-invalidation snapshot once it resolves — and so an in-flight phase 2
  // stops merging AND stops walking.
  entry.generation += 1;
  entry.refreshPromise = undefined;
  // Soft invalidation: keep serving the last snapshot but mark it stale so the
  // next ensure() bypasses the TTL fast-path and refreshes.
  if (entry.snapshot) entry.snapshot = { ...entry.snapshot, builtAt: 0 };
}

export function dropSqliteSchema(connectionId: string): void {
  // Mutate the entry in place (rather than deleting the map key) so a refresh
  // whose closure still holds this entry object observes the bumped generation
  // and skips its stale publish/cache.
  const entry = cacheByConnectionId.get(connectionId);
  if (!entry) return;
  entry.generation += 1;
  entry.snapshot = undefined;
  entry.refreshPromise = undefined;
  entry.phase2 = undefined;
  entry.columnsLoaded = undefined;
}

/**
 * Test seam — seed the native SQLite schema cache synchronously without
 * standing up an Electron bridge. Used by the dialect-native SQLite
 * resolver tests to exercise version-gated branches that read
 * `serverVersion` from the native cache. Gated on test mode
 * (`SQL_STUDIO_TEST=1` or `NODE_ENV=test`).
 */
export function injectSqliteSchemaForTest(
  connectionId: string,
  snapshot: SqliteSchemaSnapshot,
): void {
  if (typeof process !== 'undefined') {
    const isTest = process.env?.SQL_STUDIO_TEST === '1' || process.env?.NODE_ENV === 'test';
    if (!isTest) {
      throw new Error(
        'injectSqliteSchemaForTest is only available in test mode (SQL_STUDIO_TEST=1 or NODE_ENV=test).',
      );
    }
  }
  const entry = entryFor(connectionId);
  entry.snapshot = snapshot;
  entry.lastError = undefined;
}

export function __resetSqliteSchemaCacheForTest(): void {
  cacheByConnectionId.clear();
}

/**
 * Phase 1 — the `sqlite_master` relation list plus the version and
 * attached-database probes. Publishes the moment it lands and kicks phase 2
 * without awaiting it.
 */
async function runSqlitePhase1(
  entry: CacheEntry,
  options: { connectionId: string; database: string },
): Promise<SqliteSchemaSnapshot> {
  const { connectionId, database } = options;
  const generation = entry.generation + 1;
  entry.generation = generation;
  const failures = createLoadFailures();
  const shouldAbort = (): boolean => entry.generation !== generation;
  // The record describes THIS load, not the previous one (R3).
  beginIntrospectionLoad(connectionId, database);

  const [serverVersion, attachedDatabases] = await Promise.all([
    fetchSqliteServerVersion(connectionId, database).catch((error: unknown) => {
      recordLoadFailure(failures, 'serverinfo', error);
      return '';
    }),
    fetchSqliteAttachedDatabases(connectionId, database).catch((error: unknown) => {
      recordLoadFailure(failures, 'attached', error);
      return [] as SqliteAttachedDatabase[];
    }),
  ]);

  const relations: SqliteRelationEntry[] = [];
  let relationsFailed = false;
  await fetchSqliteRelationPages(
    connectionId,
    database,
    attachedDatabases,
    (page) => {
      relations.push(...page);
    },
    { shouldAbort },
  ).catch((error: unknown) => {
    relationsFailed = true;
    recordLoadFailure(failures, 'relations', error);
  });

  // Same-scope only: a database switch must not carry A's columns onto B's
  // same-named tables (release review 1.5.0, F4).
  const previous = entry.snapshot?.database === database ? entry.snapshot : undefined;
  // Stale-while-revalidate: keep the previous refresh's columns / indexes /
  // FK rows on relations that still exist until phase 2 replaces them.
  const objects = carryOverSqliteIntrospection(
    previous ? previous.tables.concat(previous.views) : undefined,
    relations,
  );

  const snapshot: SqliteSchemaSnapshot = {
    connectionId,
    database,
    builtAt: Date.now(),
    tables: objects.filter((o) => o.type === 'table'),
    views: objects.filter((o) => o.type === 'view'),
    objectsByNameLower: {},
    columnsByObjectNameLower: {},
    columnIndexByNameLower: {},
    routinesByNameLower: {},
    serverVersion,
    attachedDatabases,
    phase: 'tables',
  };

  if (relationsFailed) {
    return publishDegraded(entry, generation, {
      previous,
      built: snapshot,
      failures,
      engine: 'sqlite',
      project: projectSqliteSnapshot,
    });
  }

  const live = await createSqliteLiveSnapshot(snapshot);
  // A drop / newer refresh landed while we were projecting — discard.
  if (entry.generation !== generation) return snapshot;

  entry.snapshot = snapshot;
  entry.lastError = undefined;
  publishThrottled(entry, live.projected, true);
  entry.phase2 = runSqlitePhase2(entry, live, generation, failures, relations);
  return snapshot;
}

/**
 * Phase 2 — the per-table PRAGMA walk, batch by batch, merged incrementally
 * and re-published as batches land. Never rejects: a failure degrades to
 * whatever batches already merged and is named in the
 * `feature:schema_snapshot_partial` event.
 */
async function runSqlitePhase2(
  entry: CacheEntry,
  live: SqliteLiveSnapshot,
  generation: number,
  failures: LoadFailures,
  relations: readonly SqliteRelationEntry[],
): Promise<void> {
  const { connectionId, database } = live.native;
  const superseded = (): boolean => entry.generation !== generation;

  try {
    await fetchSqliteRelationDetailBatches(
      connectionId,
      database,
      relations,
      (batch: readonly SqliteHydratedRelation[]) => {
        if (superseded()) return;
        applySqliteRelationBatch(live, batch);
        publishThrottled(entry, live.projected, false);
      },
      { shouldAbort: superseded },
    );
  } catch (error) {
    recordLoadFailure(failures, 'detail', error);
  }

  if (superseded()) return;
  live.native.phase = 'complete';
  markLoadOutcome(entry, failures.missing, { connectionId, database });
  publishThrottled(entry, live.projected, true);
  reportPartialSnapshot('sqlite', failures);
}

export async function ensureSqliteSchema(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<SqliteSchemaSnapshot> {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const entry = entryFor(options.connectionId);

  // The cache is keyed by connection, but a snapshot describes ONE database.
  // With the day-scale TTL a database switch (or an ATTACH-driven re-open
  // under a different main database) would otherwise keep serving the previous
  // database's objects forever, so a scope mismatch counts as a miss.
  const scopeMatches = entry.snapshot?.database === options.database;

  if (
    !options.forceRefresh
    && entry.snapshot
    && scopeMatches
    && isEntryFresh(entry, ttlMs)
  ) {
    // The bus is cleared independently of this cache — by the DDL invalidator
    // and by disconnect/reconnect (`invalidateConnection`). Re-publish here
    // whenever the bus lost the entry, or the resolver would read a cold bus
    // forever.
    if (!getSchemaBusSnapshot(options.connectionId, options.database)) {
      publishThrottled(entry, projectSqliteSnapshot(entry.snapshot), true);
    }
    return entry.snapshot;
  }

  // Dedup only a refresh of the SAME database (release review 1.5.0, F4).
  if (entry.refreshPromise && entry.refreshScope === options.database) {
    return entry.refreshPromise;
  }

  const refresh = runSqlitePhase1(entry, options)
    .catch((error: unknown) => {
      entry.lastError = error instanceof Error ? error.message : 'Failed to load SQLite schema';
      throw error;
    })
    .finally(() => {
      // Only clear OUR in-flight marker: a drop/invalidate may have handed
      // `refreshPromise` to a newer refresh while we were in flight.
      if (entry.refreshPromise === refresh) {
        entry.refreshPromise = undefined;
        entry.refreshScope = undefined;
      }
    });
  entry.refreshPromise = refresh;
  entry.refreshScope = options.database;
  return refresh;
}

/**
 * Resolve once the snapshot for `connectionId` is fully loaded — phase 1
 * published AND phase 2 finished merging. Never rejects.
 */
export async function whenSqliteSnapshotComplete(connectionId: string): Promise<void> {
  await whenEntryComplete(cacheByConnectionId.get(connectionId));
}

/**
 * Resolve once the snapshot's columns have loaded — see
 * `whenEntryColumnsLoaded`. Divergence from the server engines, by design:
 * SQLite has no separate column step to wait for. Its phase 2 is ONE walk of
 * per-table PRAGMA batches, and each batch brings a table's columns, indexes
 * and FK edges together (`fetchSqliteRelationDetailBatches`), so the columns
 * are complete exactly when phase 2 is. The entry leaves `columnsLoaded`
 * unset and the shared wait falls back to `phase2`. The walk is in-process
 * (no network round trips), so this costs nothing the other engines save.
 */
export async function whenSqliteColumnsLoaded(connectionId: string): Promise<void> {
  await whenEntryColumnsLoaded(cacheByConnectionId.get(connectionId));
}

/** `ensureSqliteSchema` + `whenSqliteColumnsLoaded` — the Visual Query
 *  Editor's live schema pull (same policy as every engine: editable once
 *  tables + columns are in). */
export async function ensureSqliteSchemaWithColumns(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<SqliteSchemaSnapshot> {
  const snapshot = await ensureSqliteSchema(options);
  await whenSqliteColumnsLoaded(options.connectionId);
  return getCachedSqliteSchema(options.connectionId) ?? snapshot;
}

/** `ensureSqliteSchema` + `whenSqliteSnapshotComplete` — for callers that need
 *  the WHOLE snapshot (the coverage harness's post-DDL hook, the pill's
 *  "Reload schema" action). */
export async function ensureSqliteSchemaComplete(options: {
  connectionId: string;
  database: string;
  forceRefresh?: boolean;
  ttlMs?: number;
}): Promise<SqliteSchemaSnapshot> {
  const snapshot = await ensureSqliteSchema(options);
  await whenSqliteSnapshotComplete(options.connectionId);
  return getCachedSqliteSchema(options.connectionId) ?? snapshot;
}

/**
 * Test seam for the long-task perf gate: build a synthetic database of
 * `tableCount` relations x `columnsPerTable` columns and push it through the
 * REAL merge path — the same chunked `createSqliteLiveSnapshot` projection,
 * the same `applySqliteRelationBatch` merge in
 * `SQLITE_PRAGMA_BATCH_OBJECTS`-sized batches, the same throttled bus
 * publishes.
 */
export async function injectSyntheticSqliteSnapshotForTest(options: {
  connectionId: string;
  database: string;
  tableCount: number;
  columnsPerTable: number;
}): Promise<void> {
  const entry = entryFor(options.connectionId);
  entry.generation += 1;

  const relations: SqliteRelationEntry[] = [];
  for (let i = 0; i < options.tableCount; i += 1) {
    relations.push({ name: `synthetic_table_${i}`, type: 'table', sql: null });
    if (relations.length % INTROSPECTION_PAGE_ROWS === 0) await yieldToEventLoop();
  }

  const snapshot: SqliteSchemaSnapshot = {
    connectionId: options.connectionId,
    database: options.database,
    builtAt: Date.now(),
    tables: carryOverSqliteIntrospection(undefined, relations),
    views: [],
    objectsByNameLower: {},
    columnsByObjectNameLower: {},
    columnIndexByNameLower: {},
    routinesByNameLower: {},
    serverVersion: '3.45.0',
    attachedDatabases: [],
    phase: 'tables',
  };
  const live = await createSqliteLiveSnapshot(snapshot);
  entry.snapshot = snapshot;
  publishThrottled(entry, live.projected, true);

  // Batches of relations are SQLite's page: hydrate each one and merge the
  // batch, exactly as the PRAGMA walk does.
  const BATCH = 250;
  for (let start = 0; start < relations.length; start += BATCH) {
    const batch: SqliteHydratedRelation[] = relations
      .slice(start, start + BATCH)
      .map((relation) => ({
        ...relation,
        columns: Array.from({ length: options.columnsPerTable }, (_, c) => ({
          name: `col_${c}`,
          dataType: 'TEXT',
          isNullable: true,
          isPrimaryKey: c === 0,
          ordinalPosition: c,
        })),
        indexes: [],
        foreignKeys: [],
      }));
    applySqliteRelationBatch(live, batch);
    publishThrottled(entry, live.projected, false);
    await yieldToEventLoop();
  }
  live.native.phase = 'complete';
  publishThrottled(entry, live.projected, true);
}
